/* eslint-disable react/jsx-handler-names */
import PropTypes from "prop-types";
import CustomImage from "../../atoms/customImage";
import AddItemButton from "./addItemButton";
import Typography from "../../atoms/typography";
import { getIconUrl, updateCurrency } from "../../../utils/common";
import { RECOMMENDED } from "../../../constants/common/common";
import Drawer from "../../molecules/drawer";
import { useState } from "react";
import Portal from "../../molecules/Portal";
import { cleverTapCommonEvents, cleverTapCommonWords } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import { useABTestingView } from "../../../customHooks/clevertap/useABTestingView";
import PopOver from "../../atoms/popOver";
import { XIcon } from "@phosphor-icons/react";

/**
 * Renders an individual Add-on product card.
 * Supports layout variations for PDP (desktop/mobile) and non-PDP pages.
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - Add-on product details
 * @param {number} props.addonsQty - Quantity of selected add-on
 * @param {boolean} props.isDisabled - Disable add/remove button
 * @param {boolean} props.isFromPDP - True if rendered on PDP
 * @param {boolean} props.isMobile - True if viewed on mobile
 * @param {string} props.selectedCurrency - Active currency code
 * @param {Function} props.onRemoveItem - Handler for removing item
 * @param {Function} props.handleItemCountChange - Handler for changing quantity
 * @returns {JSX.Element} AddonCard component
 */
const AddonCard = ({
  item,
  addonsQty,
  isDisabled,
  isFromPDP,
  isMobile,
  selectedCurrency,
  onRemoveItem,
  handleItemCountChange,
  isBottomDrawer = false,
  isFromPopUp = false,
  isFromCheckout = false,
}) => {
  const imageDomain = process.env.NEXT_PUBLIC_ASSET_PREFIX;
  const [isAddOnDetailsOpen, setIsAddOnDetailsOpen] = useState(false);
  const { trackEvent } = useAnalytics();
  const { isAddOnPreview } = useABTestingView();

  /** Renders the product price with currency and discount styling. */
  const renderPrice = () => (
    <div className="text-14 font-600 text-fnp-500">
      {updateCurrency(selectedCurrency, item.price)}
      {item.listPrice > item.price && (
        <span className="ml-8 text-12 font-400 text-gray-500 line-through">
          {updateCurrency(selectedCurrency, item.listPrice)}
        </span>
      )}
    </div>
  );

  /** Renders the "Recommended" tag if the item is marked as recommended. */
  const renderRecommendedTag = () =>
    item.recommended && (
      <div className="absolute left-2 top-2 z-10 rounded bg-black-100 p-[2px] text-10 text-fnp-100">{RECOMMENDED}</div>
    );

  /**
   * Handles the click event on the image.
   */
  const onHandleImageClick = () => {
    if (isAddOnPreview) {
      const cleverTapAddonEventData = {
        page_name: isFromCheckout ? cleverTapCommonWords.CART : cleverTapCommonWords.PDP,
        page_type: isFromCheckout ? cleverTapCommonWords.CART : cleverTapCommonWords.PDP,
        product_name: item?.productName,
        pid: item?.productId,
        product_category: item?.addonCategory,
        product_price: item?.price,
        flow: "new",
      };
      trackEvent(cleverTapCommonEvents.addonPreviewed, cleverTapAddonEventData, ["clevertap"]);
      setIsAddOnDetailsOpen(true);
    }
  };

  /**
   * Handles image load error with fallback asset.
   * @param {Event} event
   */
  const onAddonImageError = (event) => {
    event.target.onerror = null;
    event.target.src = getIconUrl("/icons/placeholder-bb.png");
  };

  /** Renders the add/remove item button for the addon. */
  const renderAddButton = (
    extraClass = "ml-auto w-full",
    buttonVariant,
    btnParentClasses = "",
    isAddOnDetails = false,
  ) => (
    <AddItemButton
      isDisabled={isDisabled}
      onRemoveItem={(count) => onRemoveItem(item, count)}
      selectedItem={addonsQty}
      setSelectedItem={(count) => {
        handleItemCountChange(item, count, isDisabled, isAddOnDetails);
        // Checkout desktop uses PopOver (not Drawer); close it after add from preview.
        if (isAddOnDetails && isFromCheckout && !isMobile) {
          setIsAddOnDetailsOpen(false);
        }
      }}
      classes={extraClass}
      isFromPDP={isFromPDP}
      buttonVariant={buttonVariant}
      btnParentClasses={btnParentClasses}
      isMobile={isMobile}
      isBottomDrawer={isBottomDrawer}
      isAddOnDetails={isAddOnDetails}
      isFromPopUp={isFromPopUp}
      isFromCheckout={isFromCheckout}
    />
  );

  /** Renders the product image inside a styled container. */
  const renderImage = (containerClass = "mb-2 flex w-full items-center justify-center") => (
    <div className="relative">
      <div
        className={`${containerClass} cursor-pointer overflow-hidden rounded-lg border border-[#EEEEEE]`}
        onClick={onHandleImageClick}
      >
        <CustomImage
          src={imageDomain + item.imageUrl}
          height={360}
          width={360}
          isPriority={false}
          alt={item.productName}
          customStyle={{ width: "100%" }}
          sizes="360px"
          onError={onAddonImageError}
        />
      </div>
      {!isBottomDrawer && ((isFromPDP && isMobile) || isFromCheckout) && (
        <div className="absolute bottom-0 right-0">
          {renderAddButton("ml-auto w-full", "iconWithTextOutlined", "w-[85px]")}
        </div>
      )}
    </div>
  );

  /**
   *
   * @returns
   */
  const renderAddonDetailsContent = (item) => (
    <div className={isMobile ? "p-24 pb-0" : ""}>
      <CustomImage
        src={imageDomain + item?.largeImageUrl}
        height={360}
        width={360}
        isPriority={false}
        alt={item?.productName}
        customStyle={{ width: "100%" }}
        borderRadius={8}
        sizes="360px"
      />
      <Typography variant="body2" Tag="p" classes="w-full overflow-hidden truncate pt-12 text-12 font-500 md:text-16">
        {item.productName}
      </Typography>
      <div className={`flex w-full justify-between ${isMobile ? "mt-4" : "mt-8"}`}>
        <div className="flex w-full items-center justify-between">
          {renderPrice()}
          {renderAddButton("[&&]:w-[100px] [&&]:py-[5px]", "filled", "w-[100px]", true)}
        </div>
      </div>
    </div>
  );

  /**
   *
   * @returns {JSX.Element} Render AddonDetails Drawer component
   */
  const renderAddOnDetailsDrawer = (item) =>
    isMobile ? (
      <Drawer
        position="bottom"
        isOpen={isAddOnDetailsOpen}
        onDrawerClose={() => setIsAddOnDetailsOpen(false)}
        styles="p-0"
        isBackButtonEnabled={isBottomDrawer}
        isHideCloseBtn={isBottomDrawer}
      >
        {renderAddonDetailsContent(item)}
      </Drawer>
    ) : (
      <PopOver
        isVisible={isAddOnDetailsOpen}
        classes={"rounded-2xl max-w-[360px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-16"}
        isBackdrop={true}
      >
        <button
          onClick={() => setIsAddOnDetailsOpen(false)}
          className="absolute -right-30 -top-30 flex h-[36px] w-[36px]  items-center justify-center rounded-full bg-grey-100"
        >
          <XIcon size={25} weight="bold" />
        </button>
        {renderAddonDetailsContent(item)}
      </PopOver>
    );

  /**
   *
   * @returns {JSX.Element} AddonDetails Drawer component
   */
  const renderAddOnDetails = (item) => (
    <Portal rootId="addon-details" renderOnDidMount={true}>
      {renderAddOnDetailsDrawer(item)}
    </Portal>
  );

  /*** Desktop PDP ***/
  if (isFromPDP && !isMobile) {
    return (
      <>
        <div key={item.productId} className="relative">
          {renderRecommendedTag()}
          <div className="mb-16 flex w-full gap-16 pr-32">
            <div className="w-1/2">{renderImage()}</div>
            <div className="w-1/2">
              <Typography
                variant="body2"
                Tag="p"
                classes="w-full overflow-hidden text-ellipsis text-12 font-500 line-clamp-2 min-h-[36px]"
              >
                {item.productName}
              </Typography>

              <div className="flex w-full items-center justify-between pt-8">{renderPrice()}</div>

              {renderAddButton()}
            </div>
          </div>
        </div>
        {renderAddOnDetails(item)}
      </>
    );
  }

  /*** Mobile PDP ***/
  if (isFromPDP && isMobile) {
    return (
      <>
        <div key={item.productId} className="relative mb-16 flex w-full flex-col items-start">
          {renderRecommendedTag()}
          {renderImage()}
          <Typography variant="body2" Tag="p" classes="w-full overflow-hidden truncate pt-8 text-12 font-500">
            {item.productName}
          </Typography>
          <div className="flex w-full items-center justify-between pt-8">{renderPrice()}</div>
        </div>
        {renderAddOnDetails(item)}
      </>
    );
  }
  /*** Non-PDP (default) ***/
  return (
    <>
      <div
        key={item.productId}
        className={`relative mb-16 flex flex-none flex-col items-start p-2 ${isFromCheckout ? "w-full" : "w-[48%] md:w-[14.9%] xxl:w-[15.5%]"}`}
      >
        {renderRecommendedTag()}
        {renderImage()}
        <Typography variant="body2" Tag="p" classes="w-full overflow-hidden truncate pt-8 text-12 font-500">
          {item.productName}
        </Typography>

        <div className={`flex w-full items-center justify-between pt-8 ${isFromCheckout ? "md:pt-4" : ""}`}>
          {renderPrice()}
          {!isFromCheckout && <div>{renderAddButton("ml-auto")}</div>}
        </div>
      </div>

      {renderAddOnDetails(item)}
    </>
  );
};

AddonCard.propTypes = {
  item: PropTypes.object.isRequired,
  addonsQty: PropTypes.number,
  isDisabled: PropTypes.bool,
  isFromPDP: PropTypes.bool,
  isMobile: PropTypes.bool,
  selectedCurrency: PropTypes.object,
  onRemoveItem: PropTypes.func,
  handleItemCountChange: PropTypes.func,
  isBottomDrawer: PropTypes.bool,
  isFromPopUp: PropTypes.bool,
  isFromCheckout: PropTypes.bool,
};
export default AddonCard;
